import React, { useState, useEffect } from "react";
import Select from "react-select";
import "../../styles/projectStyles/TaskAndTeam.css";
import { fetchUserData, fetchTaskSubTask } from "../../services/API";

const TaskAndTeam = () => {
  const [selectedTasks, setSelectedTasks] = useState(() => {
    // Lazily load selectedTasks from local storage
    try {
      const savedTasks = JSON.parse(localStorage.getItem("selectedTasks"));
      const formattedEmployeeData = savedTasks.map((task) => ({
        ...task,
        employeelist: task.employeelist.filter(
          (employee, index, self) =>
            self.findIndex((e) => e.value === employee.value) === index
        ),
      }));

      return Array.isArray(formattedEmployeeData) ? formattedEmployeeData : [];
    } catch {
      return [];
    }
  });

  const [employeeData, setEmployeeData] = useState([]);
  const [taskData, setTaskData] = useState([]);
  const [validationError, setValidationError] = useState("");
  const [isValid, setIsValid] = useState(() => {
    const savedErrors =
      JSON.parse(localStorage.getItem("taskAndTeamErrors")) || {};
    return savedErrors.isValid || false;
  });

  // Fetch employee data and task data when the component mounts
  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        const response = await fetchUserData();
        const employees = response.data.filter(
          (user) => user.Job_Title === "Employee"
        );
        const formattedEmployeeData = employees.map((employee) => ({
          value: employee.ID,
          label: `${employee.First_Name} ${employee.Last_Name}`,
        }));

        console.log(formattedEmployeeData);

        setEmployeeData(formattedEmployeeData);
      } catch (error) {
        console.error("Error fetching employee data:", error);
      }
    };

    const fetchTaskData = async () => {
      try {
        const response = await fetchTaskSubTask();
        setTaskData(response.data);
      } catch (error) {
        console.error("Error fetching task and subtask data:", error);
      }
    };

    fetchEmployeeData();
    fetchTaskData();
  }, []);

  // Save selectedTasks to localStorage whenever it changes
  useEffect(() => {
    try {
      console.log("formattedEmployeeData", JSON.stringify(selectedTasks));
      localStorage.setItem("selectedTasks", JSON.stringify(selectedTasks));
      validateSelections(); // Validate whenever the selectedTasks change
    } catch (error) {
      console.error("Error saving selectedTasks to localStorage:", error);
    }
  }, [selectedTasks]);

  // Toggle task selection
  const toggleTaskSelection = (taskId) => {
    const task = taskData.find((t) => t.Task_ID === taskId);
    const updatedTasks = selectedTasks.some((t) => t.taskid === taskId)
      ? selectedTasks.filter((t) => t.taskid !== taskId)
      : [
          ...selectedTasks,
          {
            taskid: task.Task_ID,
            taskname: task.Task_Name,
            subtasklist: [],
            employeelist: [],
          },
        ];
    setSelectedTasks(updatedTasks);
  };

  // Handle subtask selection change
  const handleSubTaskChange = (selectedOptions, taskId) => {
    setSelectedTasks((prevTasks) =>
      prevTasks.map((task) =>
        task.taskid === taskId
          ? { ...task, subtasklist: selectedOptions || [] }
          : task
      )
    );
  };

  // Handle team selection change
  const handleTeamChange = (selectedOptions, taskId) => {
    setSelectedTasks((prevTasks) =>
      prevTasks.map((task) =>
        task.taskid === taskId
          ? { ...task, employeelist: selectedOptions || [] }
          : task
      )
    );
  };

  // Handle removal of a subtask
  const handleRemoveSubtask = (subtask, taskId) => {
    setSelectedTasks((prevTasks) =>
      prevTasks.map((task) =>
        task.taskid === taskId
          ? {
              ...task,
              subtasklist: task.subtasklist.filter(
                (selectedSubtask) => selectedSubtask.value !== subtask.value
              ),
            }
          : task
      )
    );
  };

  // Handle removal of a team member
  const handleRemoveTeamMember = (employee, taskId) => {
    setSelectedTasks((prevTasks) =>
      prevTasks.map((task) =>
        task.taskid === taskId
          ? {
              ...task,
              employeelist: task.employeelist.filter(
                (selectedEmployee) => selectedEmployee.value !== employee.value
              ),
            }
          : task
      )
    );
  };

  // Validate that at least one task, subtask, and team member is selected
  const validateSelections = () => {
    const errors = [];

    // Validate each task individually
    selectedTasks.forEach((task) => {
      if (!task.subtasklist || task.subtasklist.length === 0) {
        errors.push(`Task "${task.taskname}" must have at least one subtask.`);
      }
      if (!task.employeelist || task.employeelist.length === 0) {
        errors.push(
          `Task "${task.taskname}" must have at least one team member.`
        );
      }
    });

    // Determine if there are tasks selected at all
    if (selectedTasks.length === 0) {
      errors.push("At least one task must be selected.");
    }

    // Update validation state
    const isValid = errors.length === 0;
    setIsValid(isValid);

    // Update validation errors in localStorage
    localStorage.setItem("taskAndTeamErrors", JSON.stringify({ isValid }));

    // Update validation error message
    if (errors.length > 0) {
      setValidationError(errors.join(" "));
    } else {
      setValidationError(""); // Clear error if all conditions are met
    }

    return isValid; // Return the validation status
  };

  return (
    <div className="task-and-team-container">
      <div className="newtasksScreen">
        <div className="newtasks-header-container">
          <h3 className="task-title">Task and Team</h3>
          <table className="task-labels-table">
            <tbody>
              <tr>
                <td className="assign-item">
                  <span>1. Assign Tasks</span>
                </td>
                <td className="assign-item">
                  <span>2. Assign Sub-Tasks</span>
                </td>
                <td className="assign-item">
                  <span>3. Assign Team</span>
                </td>
              </tr>
            </tbody>
          </table>
          <div className="tasks-container">
            <table className="tasks-table">
              <tbody>
                {taskData.map((task) => (
                  <tr
                    key={task.Task_ID}
                    className={
                      selectedTasks.some((t) => t.taskid === task.Task_ID)
                        ? "highlighted-row"
                        : ""
                    }
                  >
                    <td className="task-selection">
                      <img
                        src={
                          selectedTasks.some((t) => t.taskid === task.Task_ID)
                            ? "/images/active.svg"
                            : "/images/inactive.svg"
                        }
                        alt="Toggle Icon"
                        className="toggle-icon"
                        onClick={() => toggleTaskSelection(task.Task_ID)}
                      />
                      {task.Task_Name}
                    </td>

                    <td className="task-selection">
                      <Select
                        isMulti
                        options={task.Subtasks.map((subtask) => ({
                          value: subtask.SUBTASK_ID,
                          label: subtask.Subtask_Name,
                        }))}
                        value={
                          selectedTasks.find((t) => t.taskid === task.Task_ID)
                            ?.subtasklist || []
                        }
                        onChange={(selected) =>
                          handleSubTaskChange(selected, task.Task_ID)
                        }
                        placeholder="Select Subtasks"
                        classNamePrefix="select"
                        hideSelectedOptions={true}
                      />

                      <div className="selected-items">
                        {selectedTasks
                          .find((t) => t.taskid === task.Task_ID)
                          ?.subtasklist.map((subtask) => (
                            <span key={subtask.value} className="selected-item">
                              {subtask.label}
                              <button
                                type="button"
                                className="remove-item"
                                onClick={() =>
                                  handleRemoveSubtask(subtask, task.Task_ID)
                                }
                              >
                                &times;
                              </button>
                            </span>
                          ))}
                      </div>
                    </td>

                    <td className="task-selection">
                      <Select
                        isMulti
                        options={employeeData}
                        value={
                          selectedTasks.find((t) => t.taskid === task.Task_ID)
                            ?.employeelist || []
                        }
                        onChange={(selected) =>
                          handleTeamChange(selected, task.Task_ID)
                        }
                        placeholder="Select Employees"
                        classNamePrefix="select"
                        hideSelectedOptions={true}
                        isSearchable={true}
                      />

                      <div className="selected-items">
                        {selectedTasks
                          .find((t) => t.taskid === task.Task_ID)
                          ?.employeelist.map((employee) => (
                            <span
                              key={employee.value}
                              className="selected-item"
                            >
                              {employee.label}
                              <button
                                type="button"
                                className="remove-item"
                                onClick={() =>
                                  handleRemoveTeamMember(employee, task.Task_ID)
                                }
                              >
                                &times;
                              </button>
                            </span>
                          ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {validationError && (
          <div className="validation-error">
            {" "}
            <span className="error-text">{validationError}</span>{" "}
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskAndTeam;
